import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";

import { ReviewWorkflowInstanceService } from "./tasks/review-workflow-instance.service";
import { NotifyService } from "./notify.service";
import { CustomerService } from "./customer.service";
import { AssignedTaskRow, AssignedToMeQuery } from "../interfaces/review-workflow-instance.interface";

@Injectable({ providedIn: "root" })
export class AssignedTasksFacadeService {
  private static readonly PAGE_SIZE = 50;
  private static readonly POLL_INTERVAL_MS = 5 * 60 * 1000;

  private readonly rowsSubject = new BehaviorSubject<AssignedTaskRow[]>([]);
  private readonly countSubject = new BehaviorSubject<number>(0);
  private readonly totalSubject = new BehaviorSubject<number>(0);
  private readonly hasMoreSubject = new BehaviorSubject<boolean>(false);
  private readonly closeRequestedSubject = new Subject<void>();
  private readonly openedSubject = new Subject<void>();

  readonly rows$: Observable<AssignedTaskRow[]> = this.rowsSubject.asObservable();
  readonly count$: Observable<number> = this.countSubject.asObservable();
  readonly total$: Observable<number> = this.totalSubject.asObservable();
  readonly hasMore$: Observable<boolean> = this.hasMoreSubject.asObservable();
  readonly closeRequested$: Observable<void> = this.closeRequestedSubject.asObservable();
  readonly opened$: Observable<void> = this.openedSubject.asObservable();

  private lastQuery: AssignedToMeQuery = {};
  private loadingMore = false;
  private generation = 0;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly reviewWorkflowInstanceService: ReviewWorkflowInstanceService,
    private readonly notifyService: NotifyService,
    private readonly translateService: TranslateService,
    customerService: CustomerService
  ) {
    customerService.getTaskSubject.subscribe(() => this.refresh());
  }

  requestClose(): void {
    this.closeRequestedSubject.next();
  }

  notifyOpened(): void {
    this.openedSubject.next();
  }

  startPolling(): void {
    if (this.pollIntervalId) return;
    this.pollIntervalId = setInterval(() => this.refresh(), AssignedTasksFacadeService.POLL_INTERVAL_MS);
  }

  stopPolling(): void {
    if (!this.pollIntervalId) return;
    clearInterval(this.pollIntervalId);
    this.pollIntervalId = null;
  }

  get rows(): AssignedTaskRow[] {
    return this.rowsSubject.value;
  }

  async load(query: AssignedToMeQuery = {}, silent = false): Promise<void> {
    this.lastQuery = query;
    const generation = ++this.generation;
    try {
      const limit = query.limit ?? AssignedTasksFacadeService.PAGE_SIZE;
      const response = await this.reviewWorkflowInstanceService.getAssignedToMe({ ...query, includeMonitoring: true, limit, skip: 0 });
      if (generation !== this.generation) return;
      this.rowsSubject.next(response.rows);
      this.countSubject.next(response.count);
      this.totalSubject.next(response.total);
      this.hasMoreSubject.next(response.rows.length < response.total);
    } catch (error) {
      if (generation !== this.generation) return;
      console.error(":::AssignedTasksFacadeService:::load:::error:::", this.errorForLog(error));
      if (!silent) {
        this.notifyService.error(this.translateService.instant("tasks.notifications_load_failed"));
      }
    }
  }

  refresh(): Promise<void> {
    return this.load(this.lastQuery, true);
  }

  async loadMore(): Promise<void> {
    if (this.loadingMore || !this.hasMoreSubject.value) return;
    this.loadingMore = true;
    const generation = this.generation;
    try {
      const limit = this.lastQuery.limit ?? AssignedTasksFacadeService.PAGE_SIZE;
      const skip = this.rowsSubject.value.length;
      const response = await this.reviewWorkflowInstanceService.getAssignedToMe({
        ...this.lastQuery,
        includeMonitoring: true,
        limit,
        skip
      });
      if (generation !== this.generation) return;
      const rows = [...this.rowsSubject.value, ...response.rows];
      this.rowsSubject.next(rows);
      this.countSubject.next(response.count);
      this.totalSubject.next(response.total);
      this.hasMoreSubject.next(response.rows.length > 0 && rows.length < response.total);
    } catch (error) {
      if (generation === this.generation) {
        console.error(":::AssignedTasksFacadeService:::loadMore:::error:::", this.errorForLog(error));
        this.notifyService.error(this.translateService.instant("tasks.notifications_load_failed"));
      }
    } finally {
      this.loadingMore = false;
    }
  }

  private errorForLog(error: unknown): unknown {
    const typed = error as { status?: number; message?: string } | null;
    return typed?.status ?? typed?.message ?? "unknown";
  }

  removeByTaskId(transcriptId: string): void {
    // a monitoring row belongs to no task
    this.rowsSubject.next(this.rowsSubject.value.filter(row => !("transcript" in row) || row.transcript !== transcriptId));
  }
}
